import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import Cookies from 'universal-cookie';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { servicesApi } from '@/services/api';
import { useCallback, useMemo } from 'react';
import { Events, type IEvents, type EventProperties } from './types';

export function useAnalytics() {
  const workspace = useCurrentWorkspace();
  const isEnabled = useIsAnalyticsEnabled();

  const track = useCallback(
    <
      Namespace extends keyof IEvents,
      E extends keyof IEvents[Namespace] & string,
      Properties extends Record<EventProperties<Namespace, E>, unknown>,
    >(
      namespace: Namespace,
      event: E,
      properties: Properties,
    ) => {
      return new Promise<void>((resolve) => {
        if (isEnabled === false || workspace == null) {
          return resolve();
        }

        const namespaceEvents = Events[namespace];
        if (namespaceEvents == null) {
          return resolve();
        }

        const eventDetails = namespaceEvents[event];
        if (eventDetails == null) {
          return resolve();
        }

        if (!hasName(eventDetails)) {
          return resolve();
        }

        const eventName = eventDetails.name;

        servicesApi // we resolve both on success and failure to avoid noise/blocking the app since users should not be concerned about this
          .trackEvent({
            params: { workspaceId: workspace.id },
            body: {
              event: eventName,
              properties: {
                ...getAnalyticsMetaProperties(),
                ...properties,
              },
            },
          })
          .then(() => resolve())
          .catch(() => resolve());
      });
    },
    [isEnabled, workspace],
  );

  return useMemo(() => ({ track, isEnabled }), [track, isEnabled]);
}

/**
 * Narrow the type to ensure the object has a `name` property of type string.
 */
function hasName(object: unknown): object is { name: string } {
  return (
    typeof object === 'object' &&
    object !== null &&
    'name' in object &&
    typeof (object as { name: unknown }).name === 'string'
  );
}

/**
 * Retrieves the Google Analytics client ID from the global `ga` object.
 */
function getGoogleAnalyticsClientID() {
  return window.ga?.getAll?.()[0]?.get('clientId');
}

/**
 * Retrieves the Segment anonymous ID from cookies.
 */
function getSegmentAnonymousId() {
  const cookies = new Cookies();
  const id = cookies.get('ajs_anonymous_id');
  if (id != null) {
    return id;
  }

  // Segment JSON.stringifies a string before saving it as a cookie,
  // so it adds extra quotes around that string. We try to parse it,
  // and if it succeeds, that will return the string, otherwise we
  // just sent the value as it is.
  try {
    return JSON.parse(id);
  } catch {
    return id;
  }
}

/**
 * Constructs meta properties for analytics events.
 */
function getAnalyticsMetaProperties() {
  return {
    path: window.location.pathname,
    referrer: document.referrer,
    search: window.location.search,
    title: document.title,
    url: window.location.href,
    user_agent: navigator.userAgent,
    segment_anonymous_id: getSegmentAnonymousId(),
    google_client_id: getGoogleAnalyticsClientID(),
  };
}

/**
 * Disables analytics tracking when we are masquerading as a user.
 */
function useIsAnalyticsEnabled() {
  const auth = useStore(authStore);

  return auth.account?.is_masq !== true;
}
